import type { Command } from '@o2d/history';
import { withPatch } from '@o2d/shared';
import type { BoardState } from '../../core/state/board-state.js';
import type { LineAnchor } from '../../core/interfaces/board-object.interface.js';
import type { LineObject } from './line.interface.js';
import { isLine } from './line.types.js';
import { mirrorLineSwap, type LineSwapSnapshot } from './line-swap-utils.js';

export type Endpoint = 'start' | 'end';

export interface EndpointSnapshot {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly startAnchor?: LineAnchor;
  readonly endAnchor?: LineAnchor;
}

/**
 * The bounding box a line with these endpoints occupies.
 *
 * Shared by `MoveEndpointCommand` and the live drag preview in
 * `LineEndpointHandlesController` so what the user sees while dragging is
 * computed exactly like what gets committed on release.
 */
export function endpointBounds(
  snap: EndpointSnapshot,
): Pick<LineObject, 'x' | 'y' | 'width' | 'height'> {
  return {
    x: Math.min(snap.x1, snap.x2),
    y: Math.min(snap.y1, snap.y2),
    width: Math.abs(snap.x2 - snap.x1),
    height: Math.abs(snap.y2 - snap.y1),
  };
}

/**
 * Move a single endpoint (start or end) of a `LineObject`.
 * The bounding box (x, y, width, height) is recomputed from the snapshot.
 * Anchors (start/end) are part of the snapshot so undo/redo also restores
 * the attachment state.
 *
 * Migrated to the immutable replace pattern: writes via
 * `state.replaceObject(id, withPatch(line, patch))` instead of the
 * deprecated `state.updateObject`.
 */
export class MoveEndpointCommand implements Command<BoardState> {
  readonly label = 'Endpunkt verschieben';

  constructor(
    private readonly id: string,
    private readonly from: EndpointSnapshot,
    private readonly to: EndpointSnapshot,
  ) {}

  private write(state: BoardState, snap: EndpointSnapshot): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;
    const patch: Partial<LineObject> = {
      x1: snap.x1,
      y1: snap.y1,
      x2: snap.x2,
      y2: snap.y2,
      ...endpointBounds(snap),
      startAnchor: snap.startAnchor,
      endAnchor: snap.endAnchor,
    };
    state.replaceObject(this.id, withPatch(obj, patch));
  }

  apply(state: BoardState): void {
    this.write(state, this.to);
  }
  revert(state: BoardState): void {
    this.write(state, this.from);
  }
}

/**
 * Snapshot of the geometry a group-scale changes on a line: both endpoints,
 * the stroke width (grows/shrinks with the group per Anke's decision) and, for
 * curved lines, the absolute control points. Anchors are intentionally NOT part
 * of the snapshot — a docked endpoint is governed by its target and re-derived
 * by AnchorSyncController, never by this command (see `keepStart`/`keepEnd`).
 */
export interface LineScaleSnapshot {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly strokeWidth: number;
  readonly curveControls?: readonly { x: number; y: number }[];
}

/**
 * Scale a `LineObject` as part of a multi-selection group scale (M2).
 *
 * Mirrors `MoveObjectsCommand`'s anchor discipline: an ANCHORED endpoint is
 * KEPT — the command reads the line fresh on every apply/revert and leaves that
 * endpoint at whatever the target-driven `AnchorSyncController` commit path has
 * put it, so the two never fight and the result is order-independent regardless
 * of whether the docked target's resize commits before or after this command.
 * A FREE endpoint (and every curve control) is written to the pre-computed
 * scaled position. The stroke width scales too, so thickness and end caps grow
 * with the group.
 */
export class ScaleLineCommand implements Command<BoardState> {
  readonly label = 'Linie skalieren';

  constructor(
    private readonly id: string,
    private readonly from: LineScaleSnapshot,
    private readonly to: LineScaleSnapshot,
  ) {}

  private write(state: BoardState, snap: LineScaleSnapshot): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;

    // Anchored endpoints are target-driven: keep the CURRENT (fresh) coordinate
    // so the AnchorSyncController commit path owns it. Only free endpoints move
    // to the pre-computed scaled position.
    const keepStart = !!obj.startAnchor;
    const keepEnd = !!obj.endAnchor;
    const x1 = keepStart ? obj.x1 : snap.x1;
    const y1 = keepStart ? obj.y1 : snap.y1;
    const x2 = keepEnd ? obj.x2 : snap.x2;
    const y2 = keepEnd ? obj.y2 : snap.y2;

    const patch: Partial<LineObject> = {
      x1,
      y1,
      x2,
      y2,
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
      strokeWidth: snap.strokeWidth,
      ...(snap.curveControls ? { curveControls: snap.curveControls } : {}),
    };
    state.replaceObject(this.id, withPatch(obj, patch));
  }

  apply(state: BoardState): void {
    this.write(state, this.to);
  }
  revert(state: BoardState): void {
    this.write(state, this.from);
  }
}

/**
 * Set the orthogonal line's middle-segment position (`midRatio`, a fraction in
 * [0,1]) in a single undo step. Written by the middle-segment drag handle on
 * release; the live drag preview repaints without touching state.
 *
 * `from`/`to` are the pre- and post-drag ratios (`undefined` == the default
 * 0.5 midpoint), so undo restores exactly the previous bend position.
 */
export class SetLineMidRatioCommand implements Command<BoardState> {
  readonly label = 'Linien-Knick verschieben';

  constructor(
    private readonly id: string,
    private readonly from: number | undefined,
    private readonly to: number | undefined,
  ) {}

  private write(state: BoardState, ratio: number | undefined): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;
    state.replaceObject(this.id, withPatch(obj, { midRatio: ratio }));
  }

  apply(state: BoardState): void {
    this.write(state, this.to);
  }
  revert(state: BoardState): void {
    this.write(state, this.from);
  }
}

/**
 * Set the `curved` line's control points (`curveControls` = absolute world
 * points) in a single undo step. Written when a control handle is dragged, or a
 * point is added (double-click line) / removed (double-click point). The live
 * drag preview repaints without touching state.
 *
 * `from`/`to` are the pre- and post-edit lists (`undefined`/empty == the default
 * midpoint), so undo restores exactly the previous shape.
 */
export class SetLineCurveCommand implements Command<BoardState> {
  readonly label = 'Kurvenpunkte';

  constructor(
    private readonly id: string,
    private readonly from: readonly { x: number; y: number }[] | undefined,
    private readonly to: readonly { x: number; y: number }[] | undefined,
  ) {}

  private write(state: BoardState, curveControls: readonly { x: number; y: number }[] | undefined): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;
    state.replaceObject(this.id, withPatch(obj, { curveControls }));
  }

  apply(state: BoardState): void {
    this.write(state, this.to);
  }
  revert(state: BoardState): void {
    this.write(state, this.from);
  }
}

/**
 * Snapshot of the swap-relevant fields of a `LineObject` taken before
 * applying a `SwapLineCapsCommand`. `apply` writes the swapped state,
 * `revert` writes this snapshot back unchanged — restoring both caps,
 * both endpoints and both anchors in a single Undo step (H4-Fix).
 */
/**
 * Alias of `LineSwapSnapshot` for backward compatibility with PRD-009
 * callers (CommandSerializer, public command-event consumers). The
 * single source of truth lives in `./line-swap-utils.ts` (PRD-010 §G5).
 */
export type SwapSnapshot = LineSwapSnapshot;

/**
 * Atomically swap startCap and endCap of a `LineObject` so the two end shapes
 * (arrow / dot / square) change sides. Endpoints and anchors stay put — the
 * shapes are drawn at the fixed endpoints, so swapping only the caps is what
 * makes them visibly move. H4-Fix: a single Undo step restores the caps.
 */
export class SwapLineCapsCommand implements Command<BoardState> {
  readonly label = 'Enden tauschen';

  constructor(
    private readonly id: string,
    private readonly _before: SwapSnapshot,
  ) {}

  /** Public read-only accessor for the target line UID (PRD-009 serializer). */
  get objectId(): string {
    return this.id;
  }

  /** Public read-only accessor used by command serialization (PRD-009). */
  get before(): SwapSnapshot {
    return this._before;
  }

  private write(state: BoardState, snap: SwapSnapshot): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;
    const patch: Partial<LineObject> = {
      x1: snap.x1,
      y1: snap.y1,
      x2: snap.x2,
      y2: snap.y2,
      x: Math.min(snap.x1, snap.x2),
      y: Math.min(snap.y1, snap.y2),
      width: Math.abs(snap.x2 - snap.x1),
      height: Math.abs(snap.y2 - snap.y1),
      startCap: snap.startCap,
      endCap: snap.endCap,
      startAnchor: snap.startAnchor,
      endAnchor: snap.endAnchor,
    };
    state.replaceObject(this.id, withPatch(obj, patch));
  }

  apply(state: BoardState): void {
    this.write(state, mirrorLineSwap(this._before));
  }
  revert(state: BoardState): void {
    this.write(state, this._before);
  }
}

/**
 * Switch a line between path strategies (`'straight'` ↔ `'orthogonal'`) in a
 * single undo step. Stage 1 flips only the `pathStrategy` discriminator — the
 * renderer re-routes the polyline from it. Stage 2 (draggable middle segment)
 * will extend `write` to seed/clear the waypoints alongside the discriminator.
 *
 * `from`/`to` are the pre- and post-image strategy ids (`undefined` == the
 * default `'straight'`), so undo restores exactly what was there before.
 */
export class SetLinePathStrategyCommand implements Command<BoardState> {
  readonly label = 'Linienform';

  constructor(
    private readonly id: string,
    private readonly from: string | undefined,
    private readonly to: string | undefined,
  ) {}

  private write(state: BoardState, strategy: string | undefined): void {
    const obj = state.getObject(this.id);
    if (!obj || !isLine(obj)) return;
    state.replaceObject(this.id, withPatch(obj, { pathStrategy: strategy }));
  }

  apply(state: BoardState): void {
    this.write(state, this.to);
  }
  revert(state: BoardState): void {
    this.write(state, this.from);
  }
}
